import { Users } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
} from "@/components/ui";
import { EmptyState } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import type { CustomerInsights } from "@/types";

interface CustomerInsightsCardProps {
  data: CustomerInsights;
}

export function CustomerInsightsCard({ data }: CustomerInsightsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Customers
        </CardTitle>
        <CardDescription>Visits and acquisition</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Visits" value={String(data.visits)} />
          <Stat label="Unique" value={String(data.uniqueCustomers)} />
          <Stat label="New" value={String(data.newCustomers)} />
        </div>
        <Separator />
        <div>
          <h4 className="mb-2 text-sm font-semibold text-muted-foreground">
            Top customers
          </h4>
          {data.topCustomers.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No linked customers"
              description="Link customers to orders to see who your regulars are"
            />
          ) : (
            <ul className="space-y-2">
              {data.topCustomers.map((c) => (
                <li
                  key={c.customerId}
                  className="flex items-center justify-between rounded-md border p-2 text-sm"
                >
                  <span className="truncate font-medium">{c.name}</span>
                  <span className="shrink-0 text-right text-muted-foreground">
                    {c.orderCount} orders ·{" "}
                    <span className="font-semibold text-foreground">
                      {formatCurrency(c.totalSpend)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-muted p-3 text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}
